// Starter content seeded into a brand-new onboarding moodboard so the user never
// lands on an empty canvas: the user's website (when given, it auto-enriches in the
// background), a voice note, an audience note, and an invitation to drop in more.
// Every item carries a `seed` marker until the user edits it. Once the website has
// been read, the onboarding fill rewrites the untouched notes from it and swaps the
// invitation for the site's logo and colors (backend routes/stream/onboardingFill.ts).
// The shape here matches `moodboard.create`'s item input (server assigns ids + positions).

import type { RouterInput } from '@/config/trpc'
import type { MoodboardItem } from '@backend/lib/db/schema/moodboard'
import { STARTER_GENERIC_TEXT, findSeeded } from '@backend/lib/onboarding/starterBoard'

// Input items array is optional on the wire (`.default([])`); unwrap to the
// concrete array type so we can build and spread it.
type MoodboardCreateItems = NonNullable<RouterInput['moodboard']['create']['items']>

const STARTER_NOTES: MoodboardCreateItems = [
  {
    type: 'text',
    kind: 'instruction',
    text: STARTER_GENERIC_TEXT.voice,
    seed: { slot: 'voice', source: 'generic' },
  },
  {
    type: 'text',
    kind: 'reference',
    text: STARTER_GENERIC_TEXT.audience,
    seed: { slot: 'audience', source: 'generic' },
  },
  {
    type: 'text',
    kind: 'reference',
    text: STARTER_GENERIC_TEXT.invite,
    seed: { slot: 'invite', source: 'generic' },
  },
]

/** Build the items for a new onboarding moodboard. When the user supplied a
 *  website, it leads as a URL item (kicks off background enrichment); the
 *  starter notes follow so the canvas is never empty. */
export function buildStarterTemplate(websiteUrl?: string | null): MoodboardCreateItems {
  const url = websiteUrl?.trim()
  const items: MoodboardCreateItems = []
  if (url) {
    items.push({ type: 'url', url, seed: { slot: 'website', source: 'website' } })
  }
  items.push(...STARTER_NOTES)
  return items
}

/** The website the onboarding board was seeded with, if any. */
export function onboardingWebsiteUrl(items: MoodboardItem[]): string | null {
  const website = findSeeded(items, 'website')
  return website?.type === 'url' ? website.url : null
}

/**
 * Point an existing onboarding board at a different website (or none), for the
 * "Back" path from the canvas. Swaps the seeded URL item, drops what the old site
 * contributed (logo, colors) and puts the notes it filled back to their generic
 * copy, so the new site can fill them again. Anything the user edited or added
 * stays. The result is a full items array for `moodboard.update`.
 */
export function applyOnboardingWebsite(
  items: MoodboardItem[],
  websiteUrl: string | null,
): MoodboardItem[] {
  const oldWebsite = findSeeded(items, 'website')
  const anchor = findSeeded(items, 'voice')?.position ?? { x: 320, y: 0 }
  const now = new Date().toISOString()

  const next: MoodboardItem[] = []
  let removedLogoAt: MoodboardItem['position'] | null = null
  for (const item of items) {
    if (item.id === oldWebsite?.id) continue
    if (
      item.seed?.source === 'website' &&
      (item.seed.slot === 'logo' || item.seed.slot === 'color')
    ) {
      if (item.seed.slot === 'logo') removedLogoAt = item.position
      continue
    }
    if (item.type === 'text' && item.seed?.source === 'website') {
      const slot = item.seed.slot as 'voice' | 'audience' | 'invite'
      next.push({ ...item, text: STARTER_GENERIC_TEXT[slot], seed: { slot, source: 'generic' } })
      continue
    }
    next.push(item)
  }

  // The old site's logo had replaced the invitation note; bring the note back.
  if (removedLogoAt && !findSeeded(next, 'invite')) {
    next.push({
      id: crypto.randomUUID(),
      createdAt: now,
      position: removedLogoAt,
      size: { w: 240, h: 200 },
      type: 'text',
      kind: 'reference',
      text: STARTER_GENERIC_TEXT.invite,
      seed: { slot: 'invite', source: 'generic' },
    })
  }

  if (websiteUrl) {
    next.unshift({
      id: crypto.randomUUID(),
      createdAt: now,
      position: oldWebsite?.position ?? { x: anchor.x - 320, y: anchor.y },
      size: { w: 320, h: 140 },
      type: 'url',
      kind: 'reference',
      url: websiteUrl,
      seed: { slot: 'website', source: 'website' },
    })
  }
  return next
}
